import { memo, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';

/**
 * Timeline scrubber with play/pause/replay.
 * @param {{ min, max, speed, fps, color, onTick }} props
 */
function IntFigTimeLine({ min = 0, max = 100, speed = 10, fps = 10, color = '#ff76D2', onTick }) {
  const trackRef  = useRef(null);
  const thumbRef  = useRef(null);
  const intervalRef = useRef(null);
  const [trackW, setTrackW]     = useState(0);
  const [timer, setTimer]       = useState(min);
  const [running, setRunning]   = useState(false);

  /* Measure track width */
  useEffect(() => {
    if (!trackRef.current) return;
    const ro = new ResizeObserver(([e]) => setTrackW(e.contentRect.width));
    ro.observe(trackRef.current);
    return () => ro.disconnect();
  }, []);

  /* Notify parent whenever timer changes */
  useEffect(() => { onTick?.(timer); }, [timer]); // eslint-disable-line react-hooks/exhaustive-deps

  /* Auto-stop at end */
  useEffect(() => {
    if (timer >= max && running) pause();
  }); // no deps — check on every render

  const THUMB_D = 22;
  const travel  = Math.max(trackW - THUMB_D, 1);
  const thumbX  = ((timer - min) / (max - min)) * travel;
  const fillW   = (thumbX / travel) * 100;

  /* Use a ref to hold the "want to start" flag so replay() can trigger start
     after state updates without stale-closure issues. */
  const wantPlayRef = useRef(false);

  function start() {
    if (running) return;
    wantPlayRef.current = false;
    setRunning(true);
    intervalRef.current = setInterval(() => {
      setTimer(prev => {
        const next = prev + speed / fps;
        if (next >= max) { clearInterval(intervalRef.current); setRunning(false); return max; }
        return next;
      });
    }, 1000 / fps);
  }

  function pause() {
    setRunning(false);
    clearInterval(intervalRef.current);
  }

  /* Replay: reset to min and auto-start on next render via the wantPlay flag */
  function replay() {
    clearInterval(intervalRef.current);
    setRunning(false);
    wantPlayRef.current = true;
    setTimer(min);
  }

  /* Triggered after replay() resets the timer */
  useEffect(() => {
    if (wantPlayRef.current && !running) {
      wantPlayRef.current = false;
      setRunning(true);
      intervalRef.current = setInterval(() => {
        setTimer(prev => {
          const next = prev + speed / fps;
          if (next >= max) { clearInterval(intervalRef.current); setRunning(false); return max; }
          return next;
        });
      }, 1000 / fps);
    }
  }, [timer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const handleDrag = (_, pos) => {
    pause();
    const t = (pos.x / travel) * (max - min) + min;
    setTimer(Math.max(min, Math.min(max, t)));
  };

  return (
    <div className="ct__intfig-timeline" style={{ width: '100%' }}>
      {/* Play / Pause / Replay button */}
      <button
        className="ct__intfig-tl-btn"
        onClick={() => {
          if (running) pause();
          else if (timer >= max) replay();
          else start();
        }}
        aria-label={running ? 'Pause' : timer >= max ? 'Replay' : 'Play'}
        style={{ color }}
      >
        {running ? (
          /* Pause icon */
          <svg width="22" height="22" viewBox="0 0 24 24" fill={color}>
            <rect x="5" y="4" width="4" height="16" rx="1"/>
            <rect x="15" y="4" width="4" height="16" rx="1"/>
          </svg>
        ) : timer >= max ? (
          /* Replay icon */
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
            <path d="M1 4v6h6"/>
            <path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
          </svg>
        ) : (
          /* Play icon */
          <svg width="22" height="22" viewBox="0 0 24 24" fill={color}>
            <polygon points="5,3 19,12 5,21"/>
          </svg>
        )}
      </button>

      {/* Track */}
      <div ref={trackRef} className="ct__intfig-tl-track" style={{ flex: 1 }}>
        {trackW > 0 && (
          <>
            {/* Rail */}
            <div className="ct__intfig-tl-rail">
              <div style={{ position: 'absolute', inset: 0, background: color, opacity: 0.2, borderRadius: 'inherit' }} />
              <div className="ct__intfig-tl-fill" style={{ width: `${fillW}%`, background: color }} />
            </div>
            {/* Thumb */}
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
              <Draggable
                nodeRef={thumbRef}
                axis="x"
                position={{ x: thumbX, y: 0 }}
                bounds={{ left: 0, right: travel, top: 0, bottom: 0 }}
                onDrag={handleDrag}
              >
                <div
                  ref={thumbRef}
                  className="ct__intfig-tl-thumb"
                  style={{ width: THUMB_D, height: THUMB_D, top: `calc(50% - ${THUMB_D / 2}px)`, position: 'absolute' }}
                >
                  <svg width="100%" height="100%" viewBox="-0.5 -0.5 1 1">
                    <circle cx={0} cy={0} r={0.5} fill={color} fillOpacity={0.33} />
                    <circle cx={0} cy={0} r={0.25} fill={color} />
                  </svg>
                </div>
              </Draggable>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const areEqual = (prev, next) =>
  prev.min === next.min && prev.max === next.max &&
  prev.speed === next.speed && prev.fps === next.fps &&
  prev.color === next.color;

export default memo(IntFigTimeLine, areEqual);
